import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /work-permit — TH hub for Thailand Work Permit + Non-B + BOI Visa per nationality
import { createFileRoute, Link } from "@tanstack/react-router";
import { WP_NATIONALITIES, WP_REGION_LABELS_TH, type WpRegion } from "@/data/work-permit-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Briefcase, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, MapPin, Clock, Wallet } from "lucide-react";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";
const SITE_WP = "https://nyclanguageinstitute.com";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/work-permit/")({
  head: () => {
    const url = `${SITE}/work-permit`;
    const title = "Work Permit + Non-B Visa ครบทุกสัญชาติ · BOI ฟาสต์แทร็ก 7 วัน | NYC";
    const desc = "บริการ Work Permit + Non-Immigrant B + BOI/EEC Smart Visa สำหรับชาวต่างชาติทำงานในไทย — ปรึกษาฟรี ประเมินงานให้ทันที ทักไลน์ได้เลย";
    return {
      meta: [
        { title: ctaTitle(title, "/work-permit", "th") }, { name: "description", content: ctaDesc(desc, "/work-permit", "th")},
        { property: "og:title", content: ctaTitle(title, "/work-permit", "th")}, { property: "og:description", content: ctaDesc(desc, "/work-permit", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "Work Permit ไทย, Non-B Visa, BOI Smart Visa, EEC fast-track, วีซ่าทำงานชาวต่างชาติ, work permit Bangkok, BOI work permit" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks("/work-permit", "/en/work-permit"),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "th-TH",
              mainEntity: {
                "@type": "ItemList",
                numberOfItems: WP_NATIONALITIES.length,
                itemListElement: WP_NATIONALITIES.map((n, i) => ({
                  "@type": "ListItem", position: i + 1, name: `Work Permit สำหรับคน${n.nameTh}`,
                  url: `${SITE}/work-permit/${n.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Work Permit & Non-B Visa Processing",
              serviceType: "Thailand Work Permit & Non-B Visa Processing",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "12000", priceCurrency: "THB", description: "Work Permit + Non-B Visa package" },
            }),
          },
          howToSchema({
            name: "วิธีขอ Work Permit + Non-B Visa ในไทย",
            description: "ขั้นตอน 5 step การขอใบอนุญาตทำงาน + Non-B Visa",
            totalTime: "P14D",
            estimatedCost: { currency: "THB", value: "12000" },
            inLanguage: "th",
            steps: [
              { name: "ขอ Non-B Visa ที่สถานทูตไทย", text: "ยื่นจดหมายเชิญทำงาน + เอกสารบริษัท ณ สถานทูตไทยในประเทศต้นทาง 5-7 วันทำการ", url: `${SITE}/visa` },
              { name: "เข้าไทยด้วย Non-B", text: "เข้าประเทศไทยภายใน 90 วัน — อยู่ได้ 90 วันก่อนขอต่ออายุ 1 ปี" },
              { name: "ยื่น Work Permit ที่กระทรวงแรงงาน", text: "BOI/EEC ฟาสต์แทร็ก 7 วัน · MOL ปกติ 10-14 วัน — ค่าธรรมเนียม 3,000 บาท/ปี", url: `${SITE}/work-permit` },
              { name: "ต่อ Non-B 1 ปี ที่ ตม.", text: "ขอต่ออายุ Non-B 1 ปี ภายใน 30 วันก่อนหมดอายุ ณ สำนักงานตรวจคนเข้าเมือง" },
              { name: "Re-Entry Permit", text: "ขอ Multiple Re-Entry Permit 3,800 บาท เพื่อเดินทางออกนอกไทยโดยไม่เสีย Visa" },
            ],
          }),
          faqPageSchema([
            { q: "ต้องมีเงินเดือนเท่าไหร่ถึงขอ Work Permit ได้?", a: "กระทรวงแรงงานกำหนดเงินเดือนขั้นต่ำตามสัญชาติ — ยุโรป/อเมริกา/ญี่ปุ่น/ออสเตรเลีย 50,000 บาท · เอเชีย (เกาหลี/จีน/อินเดีย) 45,000 บาท · ASEAN 25,000 บาท" },
            { q: "BOI/EEC ฟาสต์แทร็กต่างจากปกติอย่างไร?", a: "บริษัทที่ได้บัตรส่งเสริม BOI หรือตั้งใน EEC (Amata, WHA, Hemaraj) ใช้ระบบ One Stop Service ที่ Chamchuri Square — ออก Work Permit ใน 7 วัน แทน 10-14 วัน + ได้ Smart Visa S/T/I/E/O" },
            { q: "ครอบครัวมาด้วยได้หรือไม่?", a: "ได้ — คู่สมรสและบุตรอายุไม่เกิน 20 ปี ขอ Non-B Dependent ได้ฟรี พร้อม Work Permit หลัก ใช้เอกสารสมรส + สูติบัตร แปล + Apostille/MFA" },
            { q: "แปลเอกสารต้องทำที่ไหน?", a: "ต้องแปลโดยนักแปลขึ้นทะเบียนกระทรวงยุติธรรม (ตามประกาศ กยธ. 2/2562) + รับรองคำแปลที่กรมการกงสุล MFA (กฎกระทรวง MFA 2562) ก่อนยื่นกรมการจัดหางาน ตาม พ.ร.ก.การบริหารจัดการการทำงานของคนต่างด้าว 2560 ม.59 — NYC ทำครบจุดเดียว 1–2 วัน เริ่ม 800 บาท/หน้า + ค่ารับรอง MFA 400 บาท/ตรา จัดเอกสารให้ครบตามเกณฑ์ของทุกหน่วยงานที่เกี่ยวข้อง" },
            { q: "เปลี่ยนนายจ้างใหม่ต้องทำอย่างไร?", a: "ต้องยกเลิก WP เดิมก่อนแล้วยื่นใหม่ — ทำได้ภายใน 15 วัน มิฉะนั้น Non-B จะถูกยกเลิกอัตโนมัติ NYC ช่วยทำ Cancellation + New WP คู่กัน" },
          ]),
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE_WP}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Work Permit / Non-B / BOI visa tooling." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(`${SITE_WP}/work-permit`)),
      ],
    };
  },
  component: WorkPermitHub,
});

function WorkPermitHub() {
  const byRegion = WP_NATIONALITIES.reduce<Record<string, typeof WP_NATIONALITIES>>((acc, n) => {
    (acc[n.region] ||= []).push(n); return acc;
  }, {});

  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="work-permit" locale="th" />
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <span>Work Permit</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Briefcase className="mr-1 inline h-3 w-3" /> Work Permit + Non-B + BOI Visa
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            Work Permit + Non-B Visa สำหรับชาวต่างชาติทำงานในไทย
          </h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            ครอบคลุม {WP_NATIONALITIES.length} สัญชาติหลัก · BOI/EEC ฟาสต์แทร็ก 7 วัน · MOL มาตรฐาน 10-14 วัน · รวมแปลเอกสาร + MFA + Apostille + ครอบครัว Dependent
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">ใช้เวลานานเท่าไหร่และเสียค่าใช้จ่ายเท่าไหร่ในการขอ Work Permit ไทย?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Work Permit ไทยใช้เวลา 7 วัน (BOI/EEC) ถึง 14 วัน (MOL ปกติ) ค่าธรรมเนียมรัฐ 3,000 บาท/ปี + ค่าบริการ NYC 12,000 บาท (รวมยื่นแทน + แปล + ขอ Non-B Dependent ภรรยา/บุตร) เกณฑ์เงินเดือนขั้นต่ำ 25,000-50,000 บาท ตามสัญชาติ</p>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><Clock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">เวลาดำเนินการ</div><div className="font-bold">7-14 วัน</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">ค่าใช้จ่ายเริ่มต้น</div><div className="font-bold">฿12,000</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><MapPin className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">รับงานทั่วไทย</div><div className="font-bold">77 จังหวัด</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">เลือกสัญชาติของคุณ</h2>
          <p className="mt-2 text-sm text-muted-foreground">แบ่งตามภูมิภาค · คลิกเพื่อดูเงินเดือนขั้นต่ำ ระยะเวลา และเงื่อนไขเฉพาะ</p>
          <div className="mt-8 space-y-10">
            {(Object.keys(byRegion) as WpRegion[]).map((region) => (
              <div key={region}>
                <h3 className="mb-4 text-lg font-bold text-primary"><MapPin className="mr-1 inline h-4 w-4" />{WP_REGION_LABELS_TH[region]}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {byRegion[region].map((n) => (
                    <Link
                      key={n.slug}
                      to="/work-permit/$nationality"
                      params={{ nationality: n.slug }}
                      className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 text-2xl">{n.flag} <span className="text-base font-bold">{n.nameTh}</span></div>
                      <div className="mt-2 text-xs text-muted-foreground">เงินเดือนขั้นต่ำ ฿{n.minSalaryTHB.toLocaleString()} · BOI {n.fastTrackDays} วัน</div>
                      <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{n.topIndustries.slice(0, 2).join(" · ")}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        ดูรายละเอียด <ArrowRight className="h-3 w-3" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการที่เกี่ยวข้อง</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Non-B Visa ตามสัญชาติ", d: "วีซ่าทำงาน 40+ ประเทศ", to: "/visa" as const },
              { t: "นิคมอุตสาหกรรม 20+ แห่ง", d: "EEC/WHA/Amata/Lat Krabang", to: "/industrial-estates" as const },
              { t: "แปลเอกสาร + รับรอง MFA", d: "ISO 17100 EN/JP/CN/KR", to: "/translate" as const },
              { t: "Apostille (Hague)", d: "60+ ประเทศคู่สัญญา", to: "/apostille" as const },
              { t: "Notary Public Attorney", d: "POA, Board Resolution", to: "/notary" as const },
              { t: "APEC Card (ABTC)", d: "21 economies fast-track", to: "/apec" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
